// PlayAllTodayButton — one-tap morning briefing. Fetches the day's
// devotional + history + good-news + person stories and plays them
// in sequence through OpenAI TTS. Spotify-style auto-advance.
//
// Sits at the top of Home above the Daily Devotional card so the
// user can start the whole 4-piece briefing without scrolling.
import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, SkipForward, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { playOnce, stopOnce, getSingletonAudio } from "../lib/singleAudio";
import { getActiveHdVoice } from "../lib/activeVoice";

const BUS_EVENT = "hw:read-aloud:start"; // shared with ReadAloudButton

const SEGMENTS = [
  { key: "devotional", endpoint: "/today/daily-devotional", textFrom: (d) => d.text,                                label: "Devotional" },
  { key: "history",    endpoint: "/today/on-this-day",      textFrom: (d) => `${d.title}. ${d.body}`,              label: "On this day" },
  { key: "good",       endpoint: "/today/good-news",        textFrom: (d) => `${d.headline}. ${d.body}`,           label: "Something good" },
  { key: "person",     endpoint: "/today/person-of-day",    textFrom: (d) => `${d.name}. ${d.story}`,              label: "Someone you should know" },
];

export default function PlayAllTodayButton() {
  const [stage, setStage] = useState("idle"); // idle | loading | playing | paused
  const [segmentIdx, setSegmentIdx] = useState(0);
  const audioRef = useRef(null);
  const cancelRef = useRef(false);
  const myIdRef = useRef("play-all-today");

  // Stop my audio whenever a single-card Read-Aloud button OR the
  // main chapter reader starts. The singleton's own playOnce() will
  // already have stopped the audio bytes; we just sync the UI state
  // and flag the cancel ref so our onended chain doesn't fire.
  useEffect(() => {
    const onStart = (e) => {
      if (e?.detail?.id === myIdRef.current) return;
      cancelRef.current = true;
      setStage("idle");
    };
    window.addEventListener(BUS_EVENT, onStart);
    // Skip-to-next bus — fired by the docked SingletonMiniPlayer when
    // the user taps its next arrow. Only act if we're the active
    // briefing (segment in flight); otherwise this event was meant
    // for a future briefing-style player.
    const onSkip = () => {
      if (stage !== "playing" && stage !== "paused" && stage !== "loading") return;
      if (segmentIdx >= SEGMENTS.length - 1) return;
      try { audioRef.current && audioRef.current.pause(); } catch (_) {}
      audioRef.current = null;
      cancelRef.current = false;
      playSegment(segmentIdx + 1);
    };
    window.addEventListener("hw:briefing:skip", onSkip);
    return () => {
      window.removeEventListener(BUS_EVENT, onStart);
      window.removeEventListener("hw:briefing:skip", onSkip);
      cancelRef.current = true;
      stopOnce();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, segmentIdx]);

  const playSegment = async (idx) => {
    if (idx >= SEGMENTS.length) { setStage("idle"); setSegmentIdx(0); return; }
    const seg = SEGMENTS[idx];
    setSegmentIdx(idx);
    try {
      const r = await api.get(seg.endpoint);
      const text = seg.textFrom(r.data);
      if (!text || cancelRef.current) return;
      const tts = await api.post(
        "/tts/speak",
        { text, voice: getActiveHdVoice(), model: "tts-1", speed: 1.0 },
        { responseType: "blob" }
      );
      if (cancelRef.current) return;
      const url = URL.createObjectURL(tts.data);
      // Use the global singleton so this briefing can't play on top
      // of any other Read-Aloud audio. Attach our event handlers to
      // the same element BEFORE we ask the singleton to play it.
      const audio = getSingletonAudio();
      audioRef.current = audio;
      audio.onended = () => {
        if (cancelRef.current) return;
        playSegment(idx + 1);
      };
      audio.onpause = () => {
        // Only flip to "paused" if it wasn't auto-paused by ended
        // or by another component taking over the singleton.
        if (audio.ended || cancelRef.current) return;
        setStage("paused");
      };
      audio.onplay = () => setStage("playing");
      window.dispatchEvent(new CustomEvent(BUS_EVENT, { detail: { id: myIdRef.current, label: `Briefing · ${seg.label}` } }));
      await playOnce(url);
    } catch (e) {
      toast.error("Couldn't load the briefing — try again");
      setStage("idle");
    }
  };

  const handleClick = async () => {
    if (stage === "playing") {
      try { audioRef.current && audioRef.current.pause(); } catch (_) {}
      return;
    }
    if (stage === "paused" && audioRef.current) {
      try {
        window.dispatchEvent(new CustomEvent(BUS_EVENT, { detail: { id: myIdRef.current, label: `Briefing · ${SEGMENTS[segmentIdx].label}` } }));
        await audioRef.current.play();
      } catch (_) {}
      return;
    }
    // Fresh start
    cancelRef.current = false;
    setStage("loading");
    await playSegment(0);
  };

  const handleSkip = () => {
    try { audioRef.current && audioRef.current.pause(); } catch (_) {}
    audioRef.current = null;
    cancelRef.current = false;
    playSegment(segmentIdx + 1);
  };

  const isActive = stage === "playing" || stage === "paused" || stage === "loading";
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="play-all-today">
      <button
        type="button"
        onClick={handleClick}
        disabled={stage === "loading"}
        data-testid="play-all-today-button"
        aria-label={stage === "playing" ? t("action.pause") : t("today.playBriefing")}
        className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-gradient-to-r from-forest to-terracotta text-cream text-[11px] font-bold tracking-[0.18em] uppercase shadow-lg hover:shadow-xl active:scale-95 disabled:opacity-60 transition-all"
      >
        {stage === "loading" ? (
          <Spinner size={12} weight="bold" className="animate-spin" />
        ) : stage === "playing" ? (
          <Pause size={12} weight="bold" />
        ) : (
          <Play size={12} weight="fill" />
        )}
        <span>
          {stage === "loading" ? t("action.loading")
           : stage === "playing" ? `${t("action.play")} — ${SEGMENTS[segmentIdx].label}`
           : stage === "paused" ? t("today.playBriefing")
           : t("today.playBriefing")}
        </span>
      </button>
      {isActive && segmentIdx < SEGMENTS.length - 1 && (
        <button
          type="button"
          onClick={handleSkip}
          data-testid="play-all-today-skip"
          aria-label="Skip to next segment"
          title="Skip to next"
          className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-forest/10 hover:bg-forest/20 text-forest active:scale-95 transition-colors"
        >
          <SkipForward size={14} weight="bold" />
        </button>
      )}
      {isActive && (
        <span className="text-[10px] tracking-wider uppercase font-semibold text-muted_ink" data-testid="play-all-today-progress">
          {segmentIdx + 1} / {SEGMENTS.length}
        </span>
      )}
    </div>
  );
}
